<template>
    <section class="datatable-wrapper" v-bind="$attrs">
        <!-- Search & Filter -->
        <div class="datatable-filters">
            <!-- Actions -->
            <div v-if="selected.length > 0" ref="dropdownMassActions" class="datatable-mass-actions">
                <!-- Selection indicator -->
                <Float
                    placement="bottom-end"
                    :offset="6"
                    composable
                    enter="transition duration-200 ease-out"
                    enter-from="opacity-0 -translate-y-1"
                    enter-to="opacity-100 translate-y-0"
                    leave="transition duration-150 ease-in"
                    leave-from="opacity-100 translate-y-0"
                    leave-to="opacity-0 -translate-y-1"
                    :show="ui.mass_actions"
                >
                    <FloatReference>
                        <Button
                            type="button"
                            color="default"
                            size="sm"
                            class="w-full"
                            @click="ui.mass_actions = !ui.mass_actions"
                        >
                            {{
                                langChoice('general.datagrid.selected.info_count', selected.length, {
                                    count: selected.length,
                                })
                            }}
                        </Button>
                    </FloatReference>
                    <FloatContent>
                        <ui-popover class="!w-full">
                            <ui-popover-section>
                                <!-- User actions -->
                                <ui-popover-segment v-if="hasActions">
                                    <ui-popover-link
                                        v-for="action in actions"
                                        :key="action.name"
                                        :clickable="true"
                                        :disabled="action.disabled(selected)"
                                        @click="runMassAction(action)"
                                    >
                                        <span class="flex items-center gap-2">
                                            {{ action.name }}
                                            <Loading :loading="runningAction === action.name" size="sm" />
                                        </span>
                                    </ui-popover-link>
                                </ui-popover-segment>
                                <!-- System actions -->
                                <ui-popover-segment>
                                    <ui-popover-link
                                        v-for="action in systemActions"
                                        :key="action.name"
                                        :clickable="true"
                                        :disabled="action.disabled(selected)"
                                        @click="action.handler(selected)"
                                    >
                                        {{ action.name }}
                                    </ui-popover-link>
                                </ui-popover-segment>
                            </ui-popover-section>
                        </ui-popover>
                    </FloatContent>
                </Float>
            </div>

            <!-- Search -->
            <div class="relative datatable-search">
                <AdminText
                    v-model="search.term"
                    type="search"
                    :placeholder="lang('general.datagrid.search')"
                    size="sm"
                />
                <template v-if="props.showSearchButton">
                    <Button
                        type="button"
                        color="default"
                        size="sm"
                        class="absolute top-0 right-0 z-10 h-full"
                        @click="searchChanged({ term: search.term })"
                    >
                        <icon icon="hero/outline/magnifying-glass" class="w-4 h-4"></icon>
                    </Button>
                </template>
            </div>

            <!-- Active filters -->
            <div class="hidden datatable-active-filters lg:flex">
                <template v-if="!hasActiveFilters">
                    <Button
                        v-tooltip="{
                            position: 'top',
                            message: lang('general.datagrid.no_filters'),
                        }"
                        type="button"
                        color="default"
                        size="sm"
                        class="w-full"
                        @click="addFilter"
                    >
                        <icon icon="phosphor/regular/funnel-simple" class="size-4"></icon>
                        <span class="action-label">
                            {{ lang('general.datagrid.no_filters') }}
                        </span>
                    </Button>
                </template>
                <template v-else>
                    <Button
                        v-tooltip="{
                            position: 'top',
                            message: lang('general.datagrid.clear_filters'),
                        }"
                        type="button"
                        color="default"
                        size="sm"
                        class="w-full"
                        @click="clearActiveFilters"
                    >
                        <icon icon="phosphor/regular/funnel-simple-x" class="size-4"></icon>
                        <span class="action-label">
                            {{ lang('general.datagrid.clear_filters') }}
                        </span>
                    </Button>
                </template>
            </div>

            <!-- Filters -->
            <slot name="filters"> </slot>

            <!-- Grid setup -->
            <div ref="gridSetup" class="hidden datatable-setup lg:flex">
                <Button color="default" dusk="grid-setup-button" size="sm" class="w-full" @click="openGridSetup">
                    <icon icon="hero/outline/table" class="size-5"></icon>
                </Button>
            </div>

            <!-- Additional buttons -->
            <slot name="toolbar"></slot>

            <!-- Trashed switch -->
            <div v-if="trashed" ref="dropdownTrashed" class="hidden datatable-setup lg:flex">
                <Float
                    placement="bottom-end"
                    :offset="6"
                    portal
                    composable
                    enter="transition duration-200 ease-out"
                    enter-from="opacity-0 -translate-y-1"
                    enter-to="opacity-100 translate-y-0"
                    leave="transition duration-150 ease-in"
                    leave-from="opacity-100 translate-y-0"
                    leave-to="opacity-0 -translate-y-1"
                    :show="ui.trashed"
                >
                    <FloatReference>
                        <Button
                            v-tooltip="{
                                position: 'top',
                                message: currentTrashedLabel,
                            }"
                            color="default"
                            dusk="grid-trash-button"
                            size="sm"
                            class="w-full"
                            @click="ui.trashed = !ui.trashed"
                        >
                            <icon icon="hero/outline/eye" class="size-5"></icon>
                            <span class="ml-1">{{ currentTrashedLabel }}</span>
                        </Button>
                    </FloatReference>

                    <FloatContent>
                        <ui-popover class="!w-full">
                            <ui-popover-section>
                                <ui-popover-segment>
                                    <ui-popover-link
                                        v-for="trashOption in trashOptions"
                                        :key="trashOption"
                                        :dusk="`grid-trashed-${trashOption.value}`"
                                        :clickable="true"
                                        :selected="trashOption.value === params.trashed"
                                        :active="trashOption.value === params.trashed"
                                        @click="changeTrashed(trashOption.value)"
                                    >
                                        {{ trashOption.label }}
                                    </ui-popover-link>
                                </ui-popover-segment>
                            </ui-popover-section>
                        </ui-popover>
                    </FloatContent>
                </Float>
            </div>

            <!-- Pagination -->
            <div v-show="!props.all" ref="dropdownPagination" class="hidden datatable-pagination-setup lg:flex">
                <div class="datatable-pagination-setup-inner">
                    <Float
                        placement="bottom-end"
                        :offset="6"
                        composable
                        enter="transition duration-200 ease-out"
                        enter-from="opacity-0 -translate-y-1"
                        enter-to="opacity-100 translate-y-0"
                        leave="transition duration-150 ease-in"
                        leave-from="opacity-100 translate-y-0"
                        leave-to="opacity-0 -translate-y-1"
                        :show="ui.pagination"
                    >
                        <FloatReference>
                            <Button
                                v-tooltip="{
                                    position: 'top',
                                    message: lang('general.datagrid.per_page'),
                                }"
                                color="default"
                                dusk="grid-pagination-button"
                                size="sm"
                                class="w-full"
                                @click="ui.pagination = !ui.pagination"
                            >
                                <span class="datatable-pagination__active-label">{{
                                    lang('general.datagrid.per_page')
                                }}</span>
                                <span class="datatable-pagination__active-value" dusk="grid-pagination-take">{{
                                    pagination?.take
                                }}</span>
                            </Button>
                        </FloatReference>

                        <FloatContent>
                            <ui-popover class="!w-full">
                                <ui-popover-section>
                                    <ui-popover-segment>
                                        <ui-popover-link
                                            v-for="paginationOption in paginationOptions"
                                            :key="paginationOption"
                                            :dusk="`grid-pagination-take-${paginationOption}`"
                                            :clickable="true"
                                            :selected="paginationOption === pagination?.take"
                                            :active="paginationOption === pagination?.take"
                                            @click="changePagination(paginationOption)"
                                        >
                                            {{ paginationOption }}
                                        </ui-popover-link>
                                    </ui-popover-segment>
                                </ui-popover-section>
                            </ui-popover>
                        </FloatContent>
                    </Float>
                </div>
            </div>
        </div>

        <!-- Filters Advanced -->
        <div class="datatable-filters-advanced">
            <slot name="filtersAdvanced">
                <DataGridFilters
                    ref="filtersRef"
                    :columns="filterable"
                    :params="params"
                    :has-active-filters="hasActiveFilters"
                    @filters-changed="filtersChanged"
                >
                    <template v-for="slot in filterable" #[`filter-field-${slot.slug}`]="slotProps">
                        <slot :name="`filter-field-${slot.slug}`" v-bind="slotProps"></slot>
                    </template>
                    <template v-for="slot in filterable" #[`filter-operator-${slot.slug}`]="slotProps">
                        <slot :name="`filter-operator-${slot.slug}`" v-bind="slotProps"></slot>
                    </template>
                </DataGridFilters>
            </slot>
        </div>

        <!-- Grids -->
        <DataGridGrids
            v-if="props.grids && props.grids.length > 0"
            :grids="props.grids"
            :current-filters="params.filters"
            :current-search="search.term"
            @grid-selected="onGridSelected"
        />

        <!-- Data -->
        <DataTable
            :entries="entries"
            :params="params"
            :columns="displayableColumns"
            :selected="selected"
            :sortable="sortable"
            :scrollable="scrollable"
            :sortable-options="props.sortableOptions"
            :row-styler="props.rowStyler"
            :clickable="props.clickable"
            @sort="sort"
            @sorted="sorted"
            @select="selectUpdated"
            @selected="selectedUpdated"
            @row-contextmenu="onRowContextMenu"
            @row-context="(payload) => emit('row-context', payload)"
        >
            <template v-for="slot in displayableColumns" #[`heading-${slot.slug}`]="slotProps">
                <slot :name="`heading-${slot.slug}`" v-bind="slotProps"></slot>
            </template>

            <template v-for="slot in displayableColumns" #[`cell-${slot.slug}`]="slotProps">
                <slot :name="`cell-${slot.slug}`" v-bind="slotProps"></slot>
            </template>

            <template #after-row="slotProps">
                <slot name="after-row" v-bind="slotProps"></slot>
            </template>
        </DataTable>

        <!-- Pagination — the range shows for any non-empty result set, page
             buttons only when there is more than one page. -->
        <div v-if="pagination?.total > 0" class="datatable-pagination__nav">
            <div class="datatable-pagination__range">
                {{ paginationRangeText }}
            </div>
            <ol v-if="hasPagination" class="datatable-pagination__list">
                <li
                    class="datatable-pagination"
                    :class="{
                        'datatable-pagination__item': hasPreviousPage,
                        'datatable-pagination__item--inactive': !hasPreviousPage,
                    }"
                    @click="previousPage"
                >
                    <icon icon="hero/outline/chevron-left" class="w-4 h-4"></icon>
                </li>
                <template v-for="page in displayedPages" :key="page">
                    <li
                        v-if="page !== '...'"
                        class="datatable-pagination datatable-pagination__item"
                        :class="{ 'datatable-pagination__item--active': page === currentPage }"
                        @click="goToPage(page)"
                    >
                        <span class="text-xs">
                            {{ page }}
                        </span>
                    </li>
                    <li
                        v-else
                        class="datatable-pagination datatable-pagination__item datatable-pagination__item--inactive"
                    >
                        <span class="text-xs">...</span>
                    </li>
                </template>
                <li v-for="i in pagination.pages" :key="i" class="hidden">
                    {{ i }}
                </li>
                <li
                    class="datatable-pagination"
                    :class="{
                        'datatable-pagination__item': hasNextPage,
                        'datatable-pagination__item--inactive': !hasNextPage,
                    }"
                    @click="nextPage"
                >
                    <icon icon="hero/outline/chevron-right" class="w-4 h-4"></icon>
                </li>
            </ol>
        </div>
    </section>

    <GridSetup
        :columns="columnSetup"
        :show="showGridSetup"
        :data-grid-id="dataGridId"
        @close="closeGridSetup"
        @reset="resetGridSetup"
    >
        <template v-for="slot in displayableColumns" #[`heading-${slot.slug}`]="slotProps">
            <slot :name="`heading-${slot.slug}`" v-bind="slotProps"></slot>
        </template>
    </GridSetup>

    <UiContextMenu
        :items="contextMenuItems"
        :x="contextMenu.x"
        :y="contextMenu.y"
        :show="contextMenu.visible"
        @close="closeContextMenu"
    />

    <ExportModal
        v-if="exportEnabled"
        :show="exportModalShow"
        :url="computedExportUrl"
        :selected="selected"
        :has-active-filters="hasActiveFilters"
        :filters="exportFilters"
        :search="exportSearch"
        :format-options="exportFormatOptions"
        :default-mode="exportDefaultMode"
        :limit="exportLimit"
        :total="pagination?.total ?? 0"
        @close="closeExportModal"
    />

    <!-- Feed the page header status (float) with the live record count.
         No-ops on themes without a #header-status anchor. -->
    <MountedTeleport to="#header-status">
        <span class="layout-header__status-item">
            {{ langChoice('general.datagrid.records_status', pagination.total, { count: pagination.total }) }}
        </span>
    </MountedTeleport>

    <!-- Feed the app status bar (float) with record count + selection.
         No-ops on themes without a #statusbar anchor. -->
    <MountedTeleport to="#statusbar">
        <span v-if="paginationRangeText" class="layout-statusbar__item">{{ paginationRangeText }}</span>
        <span v-if="selected.length" class="layout-statusbar__item">
            {{ langChoice('general.datagrid.selected.info_count', selected.length, { count: selected.length }) }}
        </span>
    </MountedTeleport>
</template>

<script lang="ts" setup>
import { computed, reactive, ref, unref, watch, toRefs, onMounted, onUnmounted } from 'vue';
import type { Ref } from 'vue';
import { lang } from '@/lang/composable';
import NProgress from 'nprogress';
import DataTable from './DataTable.vue';
import DataGridFilters from './DataGridFilters.vue';
import DataGridGrids from './DataGridGrids.vue';
import GridSetup from './partials/GridSetup.vue';
import ExportModal from '@src/Common/Components/ExportModal.vue';
import MountedTeleport from '@src/Common/Components/MountedTeleport.vue';
import type { ContextMenuItem } from '@src/Common/Components/UI/ContextMenu/ContextMenu.vue';
import { PaginationInfo, ListingParams, Column, Action, Filter, Grid, RowStyler } from './types';
import { usePaginationDisplay } from './composables/usePaginationDisplay';
import { cloneFilters } from './gridMatching';
import { Way } from './enums';
import { onClickOutside } from '@vueuse/core';
import { useCookies } from '@vueuse/integrations/useCookies';
import { router, usePage } from '@inertiajs/vue3';
import { objectToText } from '@src/Common/Helpers/string';
import { useContextBus } from '@src/Common/Composables/useContextBus';
import { Float, FloatContent, FloatReference } from '@headlessui-float/vue';

interface FormatOption {
    id: string;
    name: string;
}

interface Props {
    prefix?: string | null;
    apiUrl?: string | null;
    columns?: Column[] | null;
    actions?: Action[] | undefined[];
    search?: string | null;
    filters?: Filter[] | null;
    grids?: Grid[] | null;
    sortable?: boolean;
    scrollable?: boolean;
    all?: boolean;
    params?: ListingParams;
    trashed?: boolean;
    trashedLabels?: { without: string; only: string; with: string } | null;
    showSearchButton?: boolean;
    query?: Record<string, unknown>;
    paginationOptions?: number[];
    sortableOptions?: object;
    rowStyler?: RowStyler;
    filterable?: Column[] | null;
    clickable?: {
        row: (row: any, $event: MouseEvent) => void;
    };
    showExport?: boolean;
    exportUrl?: string | null;
    exportFormatOptions?: FormatOption[];
}

const props = withDefaults(defineProps<Props>(), {
    prefix: null,
    apiUrl: null,
    columns: null,
    actions: () => [],
    search: null,
    filters: null,
    grids: null,
    sortable: false,
    scrollable: false,
    all: false,
    params: () => ({
        skip: (usePage().props?.skip as number) ?? 0,
        take: 10,
        sort: null,
        way: Way.ASC,
        search: null,
        filters: [],
        trashed: 'without',
    }),
    trashed: false,
    trashedLabels: null,
    showSearchButton: false,
    query: () => ({}),
    paginationOptions: () => [10, 25, 50, 100],
    sortableOptions: () => ({}),
    rowStyler: null,
    filterable: null,
    clickable: null,
    showExport: true,
    exportUrl: null,
    exportFormatOptions: () => [
        { id: 'xls', name: 'XLS' },
        { id: 'csv', name: 'CSV' },
        { id: 'json', name: 'JSON' },
        { id: 'xml', name: 'XML' },
    ],
});

const { prefix, sortable, apiUrl } = toRefs(props);

const filterable = props.filterable ? props.filterable : props.columns;

const selected = ref<number[]>([]);

const contextMenu = reactive({
    visible: false,
    x: 0,
    y: 0,
});

const emit = defineEmits<{
    (event: 'sort', column: string): void;
    (event: 'select', { id, isSelected }: { id: number; isSelected: boolean }): void;
    (event: 'selected', ids: any[]): void;
    (event: 'sorted', ids: number[] | string[]): void;
    (event: 'loaded', { params }: { params: PaginationInfo }): void;
    (event: 'permissionError', { message }: { message: string }): void;
    (event: 'row-context', payload: { event: MouseEvent; entry: any }): void;
}>();

const entries = ref([]);
const loading = ref(false);
const trashed = ref(props.trashed);

/**
 * Following data values are not connected to the grid kind of contents
 *
 * @param params
 */
const removeCommonParams = (params: ListingParams) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { skip, take, sort, way, search, filters, ...rest } = params;

    return rest;
};

/**
 * DataGrid identification
 */
const datagridRoute = route().current();
const datagridParams = route().params;

const dataGridId = computed(() => {
    const routeText = `datagrid.${datagridRoute}`;
    const paramsText = `${objectToText(removeCommonParams(datagridParams))}`;

    return paramsText ? `${routeText}.${paramsText}` : routeText;
});

const cookies = useCookies([
    'locale',
    'datagrid.params.take',
    'datagrid.params.sort',
    'datagrid.params.way',
    'datagrid.params.search',
    'datagrid.columns',
]);

const getQueryFilters = () => {
    const query = new URLSearchParams(window.location.search);
    const raw = query.get('filters');
    if (!raw) return [];

    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

const getDefaultFilters = () => {
    const filters = getQueryFilters();

    if (filters.length > 0) {
        return filters;
    }

    return props.params.filters ? props.params.filters : (props.filters ?? []);
};

const stored = ref({
    take: cookies.get('datagrid.params.take'),
    sort: cookies.get('datagrid.params.sort'),
    way: cookies.get('datagrid.params.way'),
    search: cookies.get('datagrid.params.search'),
});

const params: Ref<ListingParams> = ref({
    trashed: 'without',
    ...props.params,
    take: stored.value.take || (props.params.take ?? 10),
    filters: getDefaultFilters(),
    sort:
        stored.value.sort && stored.value.sort[dataGridId.value]
            ? stored.value.sort[dataGridId.value]
            : props.params.sort,
    way: stored.value.way && stored.value.way[dataGridId.value] ? stored.value.way[dataGridId.value] : props.params.way,
});

// Read default sort from query params
// TODO: unify with filters
// TODO: make this available for all params
const queryParams = new URLSearchParams(window.location.search);

if (queryParams.has('way')) {
    params.value.way = queryParams.get('way') as Way;
}

const pagination: Ref<PaginationInfo> = ref({
    skip: (usePage().props?.skip as number) ?? 0,
    take: 0,
    total: 0,
    pages: 0,
});

const ui = ref({
    pagination: false,
    trashed: false,
    mass_actions: false,
});

const getQuerySearch = () => {
    const query = new URLSearchParams(window.location.search);
    return query.get('search');
};

const getDefaultSearch = () => {
    const querySearch = getQuerySearch();

    if (querySearch) {
        return querySearch;
    }

    return stored.value.search ? stored.value.search[dataGridId.value] : props.search;
};

const search = ref({
    term: getDefaultSearch(),
});
let updateSearch: ReturnType<typeof setTimeout> | null = null;

const hasPagination: Ref<boolean> = computed((): boolean => pagination.value?.pages > 1);
const hasActions: Ref<boolean> = computed((): boolean => props.actions && props.actions.length > 0);

const paginationOptions = props.paginationOptions ?? [10, 25, 50, 100];
const trashOptions: {
    label: string;
    value: 'without' | 'only' | 'with';
}[] = [
    {
        label: props.trashedLabels?.without ?? lang('general.datagrid.without_trashed'),
        value: 'without',
    },
    {
        label: props.trashedLabels?.only ?? lang('general.datagrid.only_trashed'),
        value: 'only',
    },
    {
        label: props.trashedLabels?.with ?? lang('general.datagrid.with_trashed'),
        value: 'with',
    },
];

const assignParams = (options?: ListingParams) => {
    if (options) {
        params.value = {
            ...params.value,
            ...options,
        };
    }

    if (search.value.term) {
        params.value = {
            ...params.value,
            search: search.value.term,
        };
    }

    if (props.all) {
        params.value = {
            ...params.value,
            all: 1,
        };
    }
};

const storeSortToUser = (column: string, way: Way) => {
    if (typeof column !== 'undefined' && typeof way !== 'undefined') {
        if (!stored.value.sort) {
            stored.value.sort = {};
        }

        if (!stored.value.way) {
            stored.value.way = {};
        }

        const columnValue = {
            ...stored.value.sort,
            [dataGridId.value]: column,
        };

        const wayValue = {
            ...stored.value.way,
            [dataGridId.value]: way,
        };

        axios.post(route('users/preferences.save'), {
            key: 'datagrid.params.sort',
            value: columnValue,
        });

        cookies.set('datagrid.params.sort', columnValue);

        axios.post(route('users/preferences.save'), {
            key: 'datagrid.params.way',
            value: wayValue,
        });

        cookies.set('datagrid.params.way', wayValue);
    }
};

const sort = (column: string): void => {
    if (params.value.sort === column) {
        params.value = {
            ...params.value,
            way: params.value.way === Way.ASC ? Way.DESC : Way.ASC,
            skip: 0,
        };
    } else {
        params.value = {
            ...params.value,
            sort: column,
            way: Way.ASC,
            skip: 0,
        };
    }

    storeSortToUser(params.value.sort, params.value.way);
};

const selectUpdated = ({ id, isSelected }: { id: number; isSelected: boolean }): void => {
    emit('select', { id, isSelected });
};

const selectedUpdated = (ids: any[]): void => {
    selected.value = ids;
    emit('selected', ids);
};

const sorted = ({ ids }: { ids: number[] | string[] }): void => {
    emit('sorted', ids);
};

const startLoading = () => {
    NProgress.start();
    loading.value = true;
};

const finishLoading = () => {
    NProgress.done();
    loading.value = false;
};

const loadAll = async (options?: ListingParams) => {
    if (options) {
        assignParams(options);
    }

    startLoading();
    const url = apiUrl.value ? unref(apiUrl) : route(`${unref(prefix)}.admin.api.all`);
    const { data } = await window.axios.get(url, {
        params: { ...unref(params), ...props.query },
    });
    finishLoading();

    // An expired session returns the login page HTML instead of a listing —
    // reload so the server-side redirect can take the user to login.
    if (!Array.isArray(data?.data)) {
        window.location.reload();

        return data;
    }

    entries.value = data.data;
    pagination.value = data.pagination ?? pagination.value;

    emit('loaded', { params: data.pagination ?? pagination.value });

    return data;
};

const all = async (params?: ListingParams) => {
    assignParams(params);
    await loadAll();

    return entries;
};

/**
 * Mass actions
 */
const closeMassActionsDropdown = () => {
    ui.value = {
        ...ui.value,
        mass_actions: false,
    };
};

const runningAction = ref<string | null>(null);

// A handler returning a Promise opts into the running spinner + close-on-finish;
// a void handler keeps the fire-and-forget behaviour and the dropdown closes right away.
const runMassAction = (action: Action): void => {
    if (runningAction.value !== null) {
        return;
    }

    const result = action.handler(selected.value);

    if (result instanceof Promise) {
        runningAction.value = action.name;

        result.finally(() => {
            runningAction.value = null;
            closeMassActionsDropdown();
        });
    } else {
        closeMassActionsDropdown();
    }
};

const dropdownMassActions = ref(null);
onClickOutside(dropdownMassActions, () => closeMassActionsDropdown());

/**
 * Export modal
 */
const exportModalShow = ref(false);
const exportDefaultMode = ref<'all' | 'selected'>('selected');

const computedExportUrl = computed(() => {
    if (props.exportUrl) return props.exportUrl;
    if (props.prefix) {
        try {
            return route(`${props.prefix}.admin.export`);
        } catch {
            return null;
        }
    }
    return null;
});

const exportEnabled = computed(() => props.showExport !== false && computedExportUrl.value !== null);
const exportFilters = computed(() => params.value.filters ?? []);
const exportSearch = computed(() => search.value.term ?? '');
const exportLimit = computed(() => (usePage().props?.exportLimit as number) ?? null);

const openExportModal = (mode: 'all' | 'selected' = 'selected') => {
    exportDefaultMode.value = mode;
    exportModalShow.value = true;
};

const closeExportModal = () => {
    exportModalShow.value = false;
};

/**
 * System actions (built-in mass actions)
 */
const systemActions = computed<Action[]>(() => {
    const actions: Action[] = [];

    // Export selected - only if export is enabled
    if (exportEnabled.value) {
        actions.push({
            name: lang('general.datagrid.export_selected'),
            disabled: () => false,
            handler: () => {
                closeMassActionsDropdown();
                openExportModal('selected');
            },
        });
    }

    // Clear selection - always available
    actions.push({
        name: lang('general.datagrid.clear_selection'),
        disabled: () => false,
        handler: () => unselectAll(),
    });

    return actions;
});

/**
 * Trashed
 */
const changeTrashed = (trashed: 'without' | 'only' | 'with') => {
    params.value = {
        ...params.value,
        trashed,
    };

    closeTrashedDropdown();
};

const closeTrashedDropdown = () => {
    ui.value = {
        ...ui.value,
        trashed: false,
    };
};

const dropdownTrashed = ref(null);
onClickOutside(dropdownTrashed, () => closeTrashedDropdown());

const currentTrashedLabel = computed(() => {
    return trashOptions.find((option) => option.value === params.value.trashed)?.label;
});

/**
 * Pagination dropdown
 */
const storePaginationToUser = (take: number) => {
    if (typeof take !== 'undefined') {
        axios.post(route('users/preferences.save'), {
            key: 'datagrid.params.take',
            value: take,
        });
    }
};

const changePagination = (take: number) => {
    params.value = {
        ...params.value,
        take,
    };

    cookies.set('datagrid.params.take', take, {
        path: '/',
    });

    closePaginationDropdown();

    storePaginationToUser(take);
};

const closePaginationDropdown = () => {
    ui.value = {
        ...ui.value,
        pagination: false,
    };
};

const dropdownPagination = ref(null);
onClickOutside(dropdownPagination, () => closePaginationDropdown());

/**
 * Bottom pagination
 */
const currentPaginationMin = computed(() => params.value.skip);
const currentPaginationMax = computed(() => params.value.skip + params.value.take);
const hasPreviousPage = computed(() => currentPaginationMin.value > 0);
const hasNextPage = computed(() => currentPaginationMax.value < (pagination.value?.total ?? 0));

const currentPage = computed(() => {
    return Math.floor(params.value.skip / params.value.take) + 1;
});

const paginationRangeText = computed(() => {
    const total = pagination.value?.total ?? 0;
    if (!total) {
        return '';
    }
    // Server-reported take is authoritative — in `all` mode it equals total
    // while the requested params.take still holds the page-size default.
    const take = pagination.value?.take || params.value.take;
    const from = currentPaginationMin.value + 1;
    const to = Math.min(currentPaginationMin.value + take, total);
    return lang('general.datagrid.pagination_range', { from, to, total });
});

const { displayedPages } = usePaginationDisplay(pagination, currentPage);

/**
 * In `:api-url` mode the grid is detached from Inertia routing, so an
 * Inertia visit re-mounts the host page and resets `params` back to
 * `:params` defaults — visually pinning the user to page 1. The params
 * watcher above already triggers `loadAll()` on `skip` change, so the
 * axios refetch alone is enough; only the `:prefix` mode (route-driven)
 * still needs the URL to track skip.
 */
const syncPaginationUrl = (): void => {
    if (apiUrl.value) return;

    const routeParams = route().params;
    const url = route(route().current(), {
        ...routeParams,
        skip: params.value.skip,
    });
    router.visit(url);
};

const goToPage = (page) => {
    if (page === '...') return;
    // Reassign params.value (not mutate `.skip` in place) — `watch(params, ...)`
    // is shallow on the ref and only fires on `.value` reassignment, otherwise
    // loadAll() never reruns and the table content stays on the previous page.
    params.value = {
        ...params.value,
        skip: (page - 1) * params.value.take,
    };
    syncPaginationUrl();
};

const previousPage = (): void => {
    if (hasPreviousPage.value) {
        params.value = {
            ...params.value,
            skip: params.value.skip - params.value.take,
        };
    }
    syncPaginationUrl();
};

const nextPage = (): void => {
    if (hasNextPage.value) {
        params.value = {
            ...params.value,
            skip: params.value.skip + params.value.take,
        };
    }
    syncPaginationUrl();
};

const selectAll = () => {
    selected.value = entries.value.map((entry) => entry.id);
};

const unselectAll = () => {
    selected.value = [];
};

/**
 * Programmatically replace the active filters (URL-synced, single reload).
 * Mirrors onGridSelected — the suppression flag prevents the DataGridFilters
 * deep watcher from firing a second debounced load.
 */
const applyFilters = (filters: Filter[]) => {
    isLoadingFromFilters = true;
    params.value.skip = 0;
    params.value.filters = cloneFilters(filters);
    storeFiltersToCurrentRoute(params.value.filters);
    all().finally(() => {
        isLoadingFromFilters = false;
    });
};

defineExpose({ all, selectAll, unselectAll, entries, pagination, applyFilters });

/**
 * Active filters
 */
const filtersRef = ref(null);

const hasActiveFilters: Ref<boolean> = computed((): boolean => {
    return (
        (typeof search.value.term !== 'undefined' && search.value.term !== null) ||
        (params.value.filters && params.value.filters.length > 0)
    );
});

const clearActiveFilters = () => {
    search.value = {
        ...search.value,
        term: null,
    };

    storeSearchToCurrentRoute(null, []);
    filtersRef.value?.removeAllFilters();
};
const addFilter = () => {
    filtersRef.value?.addFilterToEnd();
};

const pushUrl = (finalUrl: string, queryString: string) => {
    const prev = history.state ?? {};
    history.pushState({ ...prev, datagrid: { ...(prev.datagrid ?? {}), queryString } }, '', finalUrl);
};

const storeFiltersToCurrentRoute = (filters: Filter[]) => {
    let url = null;
    try {
        url = params.value.skip > 0 ? route(route().current(), { skip: params.value.skip }) : route(route().current());
    } catch {
        url = window.location.pathname;
    }

    const queryParams = new URLSearchParams();

    // Preserve existing query parameters from props.query
    if (props.query) {
        Object.entries(props.query).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                queryParams.set(key, String(value));
            }
        });
    }

    if (filters && filters.length > 0) {
        queryParams.set('filters', JSON.stringify(filters));
    }

    const queryString = queryParams.toString();
    const finalUrl = queryString ? `${url}?${queryString}` : url;

    pushUrl(finalUrl, queryString);
};

const syncFiltersFromUrl = () => {
    const urlFilters = getQueryFilters();
    const currentFiltersJson = JSON.stringify(params.value.filters ?? []);
    const urlFiltersJson = JSON.stringify(urlFilters);

    if (urlFiltersJson === currentFiltersJson) {
        return;
    }

    isLoadingFromFilters = true;
    params.value = { ...params.value, skip: 0, filters: urlFilters };
    loadAll().finally(() => {
        isLoadingFromFilters = false;
    });
};

onMounted(() => {
    window.addEventListener('popstate', syncFiltersFromUrl);
});

onUnmounted(() => {
    window.removeEventListener('popstate', syncFiltersFromUrl);
});

const storeSearchToCurrentRoute = (term: string, filters?: Filter[]) => {
    let url = null;
    try {
        url = params.value.skip > 0 ? route(route().current(), { skip: params.value.skip }) : route(route().current());
    } catch {
        url = window.location.pathname;
    }

    const queryParams = new URLSearchParams();

    // Preserve existing query parameters from props.query
    if (props.query) {
        Object.entries(props.query).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                queryParams.set(key, String(value));
            }
        });
    }

    if (typeof term !== 'undefined' && term !== null && term !== '') {
        queryParams.set('search', term);
    }

    if (filters && filters.length > 0) {
        queryParams.set('filters', JSON.stringify(filters));
    }

    const queryString = queryParams.toString();
    const finalUrl = queryString ? `${url}?${queryString}` : url;

    pushUrl(finalUrl, queryString);
};

const storeSearchToUser = (term: string) => {
    if (typeof term !== 'undefined') {
        if (!stored.value.search) {
            stored.value.search = {};
        }

        const searchValue = {
            ...stored.value.search,
            [dataGridId.value]: term,
        };

        axios
            .post(route('users/preferences.search'), {
                value: searchValue,
            })
            .then(({ data }) => {
                if (data && data.preference && data.preference.value) {
                    cookies.set('datagrid.params.search', data.preference.value);
                }
            });
    }
};

let isLoadingFromFilters = false;

const filtersChanged = (filters: Filter[]) => {
    if (isLoadingFromFilters) {
        return;
    }

    if (updateSearch) {
        clearTimeout(updateSearch);
    }

    updateSearch = setTimeout(() => {
        isLoadingFromFilters = true;
        storeFiltersToCurrentRoute(filters);

        params.value = {
            ...params.value,
            filters,
        };

        loadAll().finally(() => {
            isLoadingFromFilters = false;
        });
    }, 600);
};

const searchChanged = (value: { term: string }) => {
    if (updateSearch) {
        clearTimeout(updateSearch);
    }

    updateSearch = setTimeout(() => {
        // Preserve current filters when changing search
        const currentFilters = params.value.filters;

        // Store both search term and filters to URL
        storeSearchToCurrentRoute(value.term, currentFilters);
        storeSearchToUser(value.term);

        params.value = {
            ...params.value,
            skip: 0,
            search: value.term,
            filters: currentFilters, // Explicitly preserve filters
            sort: null,
            way: null,
        };
    }, 600);
};

/**
 * Get column setup
 */

const columnsCookie = ref(cookies.get('datagrid.columns') ?? {});

const columnSetup = computed(() => {
    if (
        typeof columnsCookie.value !== 'undefined' &&
        typeof columnsCookie.value[dataGridId.value] !== 'undefined' &&
        columnsCookie.value[dataGridId.value].length > 0
    ) {
        const setup = columnsCookie.value[dataGridId.value]
            .filter((column) => props.columns.some((c) => c.slug === column.slug))
            .map((column) => ({
                ...props.columns.find((c) => c.slug === column.slug),
                slug: column.slug,
                display: column.display,
            }));

        // Columns added to the grid after the user saved a setup would be
        // silently dropped — append them as visible so new columns show up.
        props.columns.forEach((column) => {
            if (!setup.some((c) => c.slug === column.slug)) {
                const actionsIndex = setup.findIndex((c) => c.slug === 'actions');
                setup.splice(actionsIndex === -1 ? setup.length : actionsIndex, 0, { ...column, display: true });
            }
        });

        return setup;
    }

    return props.columns;
});

const displayableColumns = computed(() => columnSetup.value.filter((column) => column.display));

// Publish the grid's live settings to the cross-navigation context bus so the
// persistent overlay debug panel (Ctrl/Cmd+Shift+D) can reflect them.
const contextBus = useContextBus();

const contextSnapshot = computed(() => ({
    route: datagridRoute,
    columns: displayableColumns.value.map((column: any) => column.slug ?? column.name ?? ''),
    sort: params.value.sort ? { column: params.value.sort, way: params.value.way ?? null } : null,
    search: params.value.search ?? null,
    filters: params.value.filters ?? [],
    perPage: params.value.take,
    page: currentPage.value,
    total: pagination.value?.total ?? 0,
}));

watch(contextSnapshot, (snapshot) => contextBus.publish('datagrid', snapshot), { immediate: true, deep: true });

onUnmounted(() => contextBus.clear('datagrid'));

/**
 * Grid setup
 */
const showGridSetup = ref(false);

const openGridSetup = () => {
    showGridSetup.value = true;
};

const closeGridSetup = (setup) => {
    if (typeof columnsCookie.value === 'undefined') {
        columnsCookie.value = {};
    }

    columnsCookie.value[dataGridId.value] = setup.columns ?? [];
    showGridSetup.value = false;
};

const resetGridSetup = () => {
    columnsCookie.value[dataGridId.value] = [];
    showGridSetup.value = false;

    router.reload();
};

/**
 * Grid selection handler
 */
const onGridSelected = (filters: Filter[]) => {
    isLoadingFromFilters = true;
    // Clone: params.filters must never alias the grid definition objects (v-model edits mutate rows in place).
    params.value.filters = cloneFilters(filters);
    storeFiltersToCurrentRoute(params.value.filters);
    all().finally(() => {
        isLoadingFromFilters = false;
    });
};

/**
 * Context menu for bulk actions
 */
const contextMenuItems = computed<ContextMenuItem[]>(() => {
    if (!props.actions || selected.value.length === 0) {
        return [];
    }

    return props.actions.map((action) => ({
        label: action.name,
        disabled: action.disabled(selected.value),
        action: () => {
            runMassAction(action);
            contextMenu.visible = false;
        },
    }));
});

const onRowContextMenu = ({ event }: { event: MouseEvent; entryId: number }) => {
    if (selected.value.length === 0 || !props.actions?.length) {
        return;
    }

    contextMenu.visible = true;
    contextMenu.x = event.clientX;
    contextMenu.y = event.clientY;
};

const closeContextMenu = () => {
    contextMenu.visible = false;
};

// Initialize
all();

// Watchers
watch(params, (value, oldValue) => {
    if (
        value.skip === oldValue.skip &&
        value.take === oldValue.take &&
        value.sort === oldValue.sort &&
        value.way === oldValue.way &&
        value.search === oldValue.search &&
        value.trashed === oldValue.trashed
    ) {
        return;
    }

    loadAll();
});

watch(props, (value, oldValue) => {
    // Skip if props haven't actually changed
    if (JSON.stringify(value) === JSON.stringify(oldValue)) {
        return;
    }

    // Only update filters from props if props.filters actually changed AND has a value
    // Don't overwrite URL filters with null/empty props.filters
    const propsFiltersChanged = JSON.stringify(value.filters) !== JSON.stringify(oldValue.filters);
    const shouldUpdateFilters = propsFiltersChanged && value.filters?.length > 0;

    if (shouldUpdateFilters) {
        params.value = {
            ...params.value,
            filters: value.filters,
        };
    }

    if (value.search && value.search !== oldValue.search) {
        params.value.sort = null;
        params.value.way = null;
    }

    loadAll();
});

watch(
    search,
    (value) => {
        searchChanged(value);
    },
    { deep: true },
);
</script>
